// WAAP Analytics → Security Overview — NATIVE charts (no Superset iframe). The
// BE pre-normalizes Superset dashboard 108 ("scdn.SecurityOverview.v3") into
// typed widgets (number / pie / table / timeseries), same pipeline as Delivery.
//   GET /analytics/security-overview/charts?tz=…&range=…&domains=…
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';

/** Time-range options for Security Overview — Superset NATIVE relative ranges
 *  (parsed server-side into from/to), so the exact strings matter. */
export const SECURITY_TIME_RANGES = [
  'Last 1 hour',
  'Last 4 hours',
  'Last 12 hours',
  'Last 24 hours',
  'Last 7 days',
  'Last 30 days',
] as const;
export type SecurityTimeRange = (typeof SECURITY_TIME_RANGES)[number];

/** Read the normalized Security Overview widgets for a website's account.
 *  `domains` is the optional hostname filter (empty = all). Returns null when
 *  the board is not configured server-side (400/404/501). */
export function useWaapSecurityCharts(
  domainId: string | undefined,
  range = 'Last 4 hours',
  domains: string[] = [],
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.securityOverviewCharts), range, domainsKey] as const,
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, range });
        if (domainsKey) params.set('domains', domainsKey);
        const path = waapPath(domainId as string, WAAP_AREAS.securityOverviewCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(`${path}?${params.toString()}`);
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
